import React, { useState, useEffect } from 'react';
import {
  Paper,
  Box,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Autocomplete,
  Avatar,
  InputAdornment
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import axios from 'axios';

interface Contact {
  id: string;
  phoneNumber: string;
  name: string;
}

interface Group {
  id: string;
  name: string;
  memberCount: number;
}

interface ScheduledMessage {
  id: string;
  recipientType: 'contact' | 'group';
  recipientName?: string;
  recipientPhone?: string;
  messageText: string;
  nextExecutionTime: string;
  recurrencePattern: 'once' | 'weekly' | 'bi-weekly' | 'monthly';
  status: 'active' | 'paused' | 'completed' | 'failed';
}

interface MessageTemplate {
  id: string;
  name: string;
  body: string;
}

interface SchedulerProps {
  twilioNumber: string;
  onViewHistory?: (messageId: string) => void;
}

type RecipientType = 'phone' | 'contact' | 'group';
type RecurrencePattern = 'once' | 'weekly' | 'bi-weekly' | 'monthly';

const ITEMS_PER_PAGE = 10;

const Scheduler: React.FC<SchedulerProps> = ({ twilioNumber, onViewHistory }) => {
  const [scheduledMessages, setScheduledMessages] = useState<ScheduledMessage[]>([]);
  const [filteredMessages, setFilteredMessages] = useState<ScheduledMessage[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMessage, setEditingMessage] = useState<ScheduledMessage | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Form state
  const [recipientType, setRecipientType] = useState<RecipientType>('contact');
  const [selectedContacts, setSelectedContacts] = useState<Contact[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<Group[]>([]);
  const [phoneNumbers, setPhoneNumbers] = useState('');
  const [messageText, setMessageText] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [recurrencePattern, setRecurrencePattern] = useState<RecurrencePattern>('once');

  // Template state
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<MessageTemplate | null>(null);

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const characterCount = messageText.length;
  const maxCharacters = 1600;
  const segmentCount = Math.ceil(characterCount / 160) || 0;

  useEffect(() => {
    loadScheduledMessages(true); loadContacts(); loadGroups(); loadTemplates();
    // Poll for status updates every 15 seconds
    const interval = setInterval(() => { loadScheduledMessages(false); }, 15000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => { filterMessages(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, scheduledMessages]);

  const filterMessages = () => {
    if (!searchQuery.trim()) { setFilteredMessages(scheduledMessages); return; }
    const q = searchQuery.toLowerCase();
    setFilteredMessages(scheduledMessages.filter(m =>
      (m.recipientName && m.recipientName.toLowerCase().includes(q)) ||
      m.messageText.toLowerCase().includes(q)
    ));
    setCurrentPage(1);
  };

  const loadScheduledMessages = async (showLoader = true) => {
    try {
      if (showLoader) setLoading(true);
      setError('');
      const response = await axios.get('/api/scheduled-messages');
      setScheduledMessages(response.data.scheduledMessages || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load scheduled messages');
    } finally { setLoading(false); }
  };

  const loadContacts = async () => {
    try { const r = await axios.get('/api/contacts'); setContacts(r.data.contacts || []); } catch (err) {}
  };
  const loadGroups = async () => {
    try { const r = await axios.get('/api/groups'); setGroups(r.data.groups || []); } catch (err) {}
  };
  const loadTemplates = async () => {
    try { const r = await axios.get('/api/templates'); setTemplates(r.data.templates || []); } catch (err) {}
  };

  const handleOpenDialog = (message?: ScheduledMessage) => {
    if (message) {
      setEditingMessage(message);
      setMessageText(message.messageText);
      setRecurrencePattern(message.recurrencePattern);
      const execDate = new Date(message.nextExecutionTime);
      setScheduledDate(execDate.toISOString().split('T')[0]);
      setScheduledTime(execDate.toTimeString().slice(0, 5));
    } else {
      setEditingMessage(null);
      resetForm();
    }
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleCloseDialog = () => { setDialogOpen(false); setEditingMessage(null); resetForm(); setFormErrors({}); };

  const resetForm = () => {
    setRecipientType('contact'); setSelectedContacts([]); setSelectedGroups([]);
    setPhoneNumbers(''); setSelectedTemplate(null); setMessageText(''); setScheduledDate(''); setScheduledTime(''); setRecurrencePattern('once');
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (recipientType === 'phone') {
      const nums = phoneNumbers.split(',').map(n => n.trim()).filter(n => n);
      if (nums.length === 0) errors.recipient = 'Please enter at least one phone number';
      else {
        const phoneRegex = /^\+?[1-9]\d{1,14}$/;
        for (const num of nums) {
          if (!phoneRegex.test(num.replace(/[\s\-()]/g, ''))) { errors.recipient = `Invalid number: ${num}`; break; }
        }
      }
    }
    if (recipientType === 'contact' && selectedContacts.length === 0) errors.recipient = 'Please select at least one contact';
    if (recipientType === 'group' && selectedGroups.length === 0) errors.recipient = 'Please select at least one group';
    if (!messageText.trim()) errors.messageText = 'Message text is required';
    if (!scheduledDate) errors.scheduledDate = 'Date is required';
    if (!scheduledTime) errors.scheduledTime = 'Time is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    try {
      setSaving(true); setError('');
      const localDateTime = new Date(`${scheduledDate}T${scheduledTime}`);
      const isoString = localDateTime.toISOString();
      const utcDate = isoString.split('T')[0];
      const utcTime = isoString.split('T')[1].substring(0, 5);

      if (editingMessage) {
        // For editing, just update the single message
        const payload = {
          recipientType: editingMessage.recipientType,
          recipientId: recipientType === 'contact' && selectedContacts.length > 0 ? selectedContacts[0].id : recipientType === 'group' && selectedGroups.length > 0 ? selectedGroups[0].id : editingMessage.recipientType,
          messageText: messageText.trim(),
          scheduledDate: utcDate,
          scheduledTime: utcTime,
          recurrencePattern
        };
        await axios.put(`/api/scheduled-messages/${editingMessage.id}`, payload);
        setSuccess('Scheduled message updated successfully');
      } else {
        // For new messages, create one per recipient
        let created = 0;
        if (recipientType === 'contact') {
          for (const contact of selectedContacts) {
            await axios.post('/api/scheduled-messages', {
              recipientType: 'contact', recipientId: contact.id,
              messageText: messageText.trim(), scheduledDate: utcDate, scheduledTime: utcTime, recurrencePattern
            });
            created++;
          }
        } else if (recipientType === 'group') {
          for (const group of selectedGroups) {
            await axios.post('/api/scheduled-messages', {
              recipientType: 'group', recipientId: group.id,
              messageText: messageText.trim(), scheduledDate: utcDate, scheduledTime: utcTime, recurrencePattern
            });
            created++;
          }
        } else if (recipientType === 'phone') {
          // For phone numbers, create as contact-type with the first contact that matches, or just use the first
          const nums = phoneNumbers.split(',').map(n => n.trim().replace(/[\s\-()]/g, '')).filter(n => n);
          for (const num of nums) {
            const matchingContact = contacts.find(c => c.phoneNumber === num || c.phoneNumber === '+' + num);
            if (matchingContact) {
              await axios.post('/api/scheduled-messages', {
                recipientType: 'contact', recipientId: matchingContact.id,
                messageText: messageText.trim(), scheduledDate: utcDate, scheduledTime: utcTime, recurrencePattern
              });
            } else {
              // If no matching contact, we need to send directly — but the scheduler only supports contact/group IDs
              // Create the message with a placeholder and note
              setError(`Phone number ${num} is not in your contacts. Please add it as a contact first to schedule messages.`);
              setSaving(false);
              return;
            }
            created++;
          }
        }
        setSuccess(`${created} message${created !== 1 ? 's' : ''} scheduled successfully`);
      }
      handleCloseDialog(); loadScheduledMessages();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save scheduled message');
    } finally { setSaving(false); }
  };

  const handleDelete = async (message: ScheduledMessage) => {
    if (!window.confirm('Are you sure you want to delete this scheduled message?')) return;
    try {
      setError('');
      await axios.delete(`/api/scheduled-messages/${message.id}`);
      setSuccess('Scheduled message deleted'); loadScheduledMessages();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) { setError(err.response?.data?.error || 'Failed to delete'); }
  };

  const handleRetry = async (message: ScheduledMessage) => {
    try {
      setError('');
      await axios.patch(`/api/scheduled-messages/${message.id}/resume`);
      setSuccess('Message retried successfully'); loadScheduledMessages();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) { setError(err.response?.data?.error || 'Failed to retry'); }
  };

  const formatScheduledTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const month = date.toLocaleString('en-US', { month: 'short' });
    const day = date.getDate().toString().padStart(2, '0');
    const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return { dateStr: `${month} ${day}, ${time}`, full: date.toLocaleString() };
  };

  const getInitials = (name: string) => {
    const parts = name.trim().split(' ');
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
  };

  const getAvatarColor = (name: string) => {
    const colors = ['#6c5ce7', '#00b894', '#e17055', '#0984e3', '#fdcb6e', '#e84393', '#00cec9'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  };

  const getStatusChip = (status: string) => {
    const config: Record<string, { label: string; bgcolor: string; color: string }> = {
      active: { label: 'PENDING', bgcolor: '#f0f0f5', color: '#555' },
      paused: { label: 'PAUSED', bgcolor: '#fff3e0', color: '#e65100' },
      completed: { label: 'SENT', bgcolor: '#e8f5e9', color: '#2e7d32' },
      failed: { label: 'FAILED', bgcolor: '#fce4ec', color: '#c62828' },
    };
    const c = config[status] || config.active;
    return <Chip label={c.label} size="small" sx={{ bgcolor: c.bgcolor, color: c.color, fontWeight: 600, fontSize: 10, height: 24, letterSpacing: 0.5 }} />;
  };

  // Pagination
  const totalPages = Math.ceil(filteredMessages.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedMessages = filteredMessages.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 1.5 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#2d3436', mb: 0.5, fontSize: { xs: '1.25rem', md: '2.125rem' } }}>Scheduled Messages</Typography>
          <Typography variant="body2" sx={{ color: '#999' }}>Manage and automate your outgoing communications.</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search scheduled..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ width: { xs: '100%', sm: 180 }, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#999' }} /></InputAdornment>,
            }}
          />
          <Button
            variant="contained"
            startIcon={<ScheduleIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ bgcolor: '#6c5ce7', textTransform: 'none', fontWeight: 600, borderRadius: 2, px: { xs: 2, md: 3 }, fontSize: { xs: '0.8rem', md: '0.875rem' }, whiteSpace: 'nowrap', '&:hover': { bgcolor: '#5a4bd1' } }}
          >
            New Schedule
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Table */}
      <Paper sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', maxWidth: '100%' }}>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#fafafa' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: '#888', letterSpacing: 0.5 }}>Recipient</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: '#888', letterSpacing: 0.5 }}>Message Preview</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: '#888', letterSpacing: 0.5 }}>Scheduled Time</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: '#888', letterSpacing: 0.5 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: '#888', letterSpacing: 0.5 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedMessages.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                      {searchQuery ? 'No scheduled messages found' : 'No scheduled messages yet. Click "Create New Schedule" to get started.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedMessages.map((message) => {
                  const name = message.recipientName || 'Unknown';
                  const { dateStr } = formatScheduledTime(message.nextExecutionTime);
                  return (
                    <TableRow key={message.id} sx={{ '&:hover': { bgcolor: '#f9f9fc' } }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar sx={{ bgcolor: getAvatarColor(name), width: 36, height: 36, fontSize: 13, fontWeight: 600 }}>
                            {getInitials(name)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#2d3436', lineHeight: 1.2 }}>{name}</Typography>
                            {message.recipientPhone && (
                              <Typography variant="caption" sx={{ color: '#999' }}>{message.recipientPhone}</Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#555' }}>
                          {message.messageText.length > 40 ? message.messageText.substring(0, 40) + '...' : message.messageText}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#2d3436', fontWeight: 500, lineHeight: 1.2 }}>
                          {dateStr}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6c5ce7', fontWeight: 600, fontSize: 10, textTransform: 'uppercase' }}>
                          {message.recurrencePattern}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {getStatusChip(message.status)}
                      </TableCell>
                      <TableCell align="right">
                        {message.status === 'failed' && (
                          <IconButton size="small" onClick={() => handleRetry(message)} title="Retry" sx={{ color: '#999' }}>
                            <RefreshIcon fontSize="small" />
                          </IconButton>
                        )}
                        {message.status !== 'completed' && (
                          <IconButton size="small" onClick={() => handleOpenDialog(message)} title="Edit" sx={{ color: '#999' }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton size="small" onClick={() => handleDelete(message)} title="Delete" sx={{ color: '#999', '&:hover': { color: '#e74c3c' } }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {filteredMessages.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, py: 2, borderTop: '1px solid #f0f0f0' }}>
            <Typography variant="body2" sx={{ color: '#999' }}>
              Showing <strong>{paginatedMessages.length}</strong> of <strong>{filteredMessages.length}</strong> scheduled messages
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)} sx={{ textTransform: 'none', color: '#999' }}>
                Previous
              </Button>
              <Button size="small" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)} sx={{ textTransform: 'none', color: '#555' }}>
                Next
              </Button>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingMessage ? 'Edit Scheduled Message' : 'Schedule New Message'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth error={!!formErrors.recipient}>
              <InputLabel>Recipient Type</InputLabel>
              <Select value={recipientType} label="Recipient Type" onChange={(e) => { setRecipientType(e.target.value as RecipientType); setSelectedContacts([]); setSelectedGroups([]); setPhoneNumbers(''); }}>
                <MenuItem value="phone">Phone Number(s)</MenuItem>
                <MenuItem value="contact">Contact(s)</MenuItem>
                <MenuItem value="group">Group(s)</MenuItem>
              </Select>
            </FormControl>

            {recipientType === 'phone' && (
              <TextField
                fullWidth
                label="Phone Numbers"
                value={phoneNumbers}
                onChange={(e) => setPhoneNumbers(e.target.value)}
                error={!!formErrors.recipient}
                helperText={formErrors.recipient || 'Comma-separated for multiple (e.g. 17781234567, 16041234567)'}
                placeholder="17781234567, 16041234567"
                required
              />
            )}

            {recipientType === 'contact' && (
              <Autocomplete
                multiple
                options={contacts}
                getOptionLabel={(option) => `${option.name} (${option.phoneNumber})`}
                value={selectedContacts}
                onChange={(_, v) => setSelectedContacts(v)}
                renderInput={(params) => <TextField {...params} label="Select Contacts" error={!!formErrors.recipient} helperText={formErrors.recipient} required placeholder={selectedContacts.length === 0 ? "Search contacts..." : ""} />}
              />
            )}

            {recipientType === 'group' && (
              <Autocomplete
                multiple
                options={groups}
                getOptionLabel={(option) => `${option.name} (${option.memberCount} members)`}
                value={selectedGroups}
                onChange={(_, v) => setSelectedGroups(v)}
                renderInput={(params) => <TextField {...params} label="Select Groups" error={!!formErrors.recipient} helperText={formErrors.recipient} required placeholder={selectedGroups.length === 0 ? "Search groups..." : ""} />}
              />
            )}

            <Autocomplete
              options={templates}
              getOptionLabel={(option) => option.name}
              value={selectedTemplate}
              onChange={(_, v) => { setSelectedTemplate(v); if (v) setMessageText(v.body); }}
              renderInput={(params) => <TextField {...params} label="Message Template" placeholder="Select a template (optional)" />}
            />

            <TextField
              label="Message"
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              error={!!formErrors.messageText}
              helperText={formErrors.messageText || `${characterCount}/${maxCharacters} characters (${segmentCount} segment${segmentCount !== 1 ? 's' : ''})`}
              multiline rows={4} required fullWidth
              inputProps={{ maxLength: maxCharacters }}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Date" type="date" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)} error={!!formErrors.scheduledDate} helperText={formErrors.scheduledDate} required fullWidth InputLabelProps={{ shrink: true }} />
              <TextField label="Time" type="time" value={scheduledTime} onChange={(e) => setScheduledTime(e.target.value)} error={!!formErrors.scheduledTime} helperText={formErrors.scheduledTime} required fullWidth InputLabelProps={{ shrink: true }} />
            </Box>

            <FormControl fullWidth>
              <InputLabel>Recurrence Pattern</InputLabel>
              <Select value={recurrencePattern} label="Recurrence Pattern" onChange={(e) => setRecurrencePattern(e.target.value as RecurrencePattern)}>
                <MenuItem value="once">Once</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="bi-weekly">Bi-weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving} sx={{ bgcolor: '#6c5ce7', '&:hover': { bgcolor: '#5a4bd1' } }}>
            {saving ? <CircularProgress size={24} /> : (editingMessage ? 'Update' : 'Schedule')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Scheduler;
